/**
 * The one HTTP client. Every call is credentialed (the session lives in the HttpOnly
 * `pa_session` cookie on .nmcapps.com, so the browser must be told to send it), and a 401 from
 * any endpoint means the session is gone — the handler registered by the session provider flips
 * the app to signed-out instead of each screen inventing its own recovery.
 *
 * Typing rule: every field the API serialises from a Postgres bigint (money, counts, ids) is
 * `string` here and stays a string. Numbers are for pagination limits only.
 */

const DEFAULT_API_URL = "https://dev-api.nmcapps.com";

export const API_URL = (import.meta.env.VITE_API_URL ?? DEFAULT_API_URL).replace(/\/+$/, "");

/** bigint minor units, JSON-encoded as a string. Never parse this into a number. */
export type Minor = string;
/** Postgres bigint primary key, JSON-encoded as a string. */
export type Id = string;
export type IsoDateTime = string;

export type Severity = "INFO" | "WARN" | "ALERT";
export type ReconcileResult = "RECONCILED" | "DISCREPANT" | "INCOMPLETE";
export type DiscrepancyStatus = "OPEN" | "ACKNOWLEDGED" | "RESOLVED" | "IGNORED";
export const DISCREPANCY_STATUSES: readonly DiscrepancyStatus[] = ["OPEN", "ACKNOWLEDGED", "RESOLVED", "IGNORED"];
/** Ordering for the discrepancy list; ALERT first. CONSTANTS.md "severity". */
export const SEVERITY_ORDER: readonly Severity[] = ["ALERT", "WARN", "INFO"];

export interface User {
  id: Id;
  email: string;
  role: string;
  tenant_id: Id;
}

/** Our own recurring charge for this store (Shopify Billing API). null = never subscribed. */
export interface ConnectionSubscription {
  id: Id;
  external_id: string;
  plan_name: string;
  status: string;
  price_minor: Minor;
  currency: string;
  billing_interval: string;
  trial_days: number;
  trial_ends_at: IsoDateTime | null;
  current_period_end: IsoDateTime | null;
  /** A Shopify test charge — the subscription exists but nobody is billed. */
  is_test: boolean;
  activated_at: IsoDateTime | null;
  cancelled_at: IsoDateTime | null;
}

/**
 * The full `app_subscription` row, as `GET /connections/:id/billing` returns it. Wider than the
 * copy embedded in a connection: the billing screen also needs the approval link and the platform
 * timestamps, which the store list has no use for.
 */
export interface Subscription extends ConnectionSubscription {
  connection_id: Id;
  /** Shopify's approval page for a charge still waiting to be accepted. */
  confirmation_url: string | null;
  created_at_platform: IsoDateTime | null;
  created_at: IsoDateTime;
  updated_at: IsoDateTime;
}

/**
 * What we sell, quoted by the API. The price is NEVER written in the SPA: it lives once, in the
 * adapter's plan constants, and travels here so the offer on screen cannot drift from the charge
 * the Billing API actually creates.
 */
export interface BillingPlan {
  name: string;
  price_minor: Minor;
  currency: string;
  billing_interval: string;
  trial_days: number;
}

export interface Connection {
  id: Id;
  tenant_id: Id;
  platform: string;
  external_shop_id: string;
  display_name: string;
  status: string;
  default_currency: string | null;
  installed_at: IsoDateTime | null;
  sync_cursor: Record<string, unknown>;
  subscription: ConnectionSubscription | null;
}

export interface MoneyTotal {
  currency: string;
  amount_minor: Minor;
  count: string;
}

export interface ConnectionSummary {
  held: MoneyTotal[];
  pending: MoneyTotal[];
  /** Fees the platform kept on fully refunded orders, one entry per currency. Not a discrepancy. */
  fees_retained: MoneyTotal[];
  open_discrepancies: { severity: Severity; count: string }[];
  /**
   * Whether R8 can see this store's fees at all. One entry per (line_kind, currency) that has
   * fee-bearing lines; `priced` is how many a fee schedule covers. `priced` < `lines` means the
   * fee check is off for that slice — the rule is silent, not clean.
   */
  fee_coverage: { line_kind: string; native_subtype: string | null; currency: string; lines: string; priced: string }[];
  disputes_open: string;
  last_payout: {
    external_id: string;
    status: string;
    issued_at: IsoDateTime | null;
    declared_net_minor: Minor;
    currency: string;
    result: ReconcileResult | null;
  } | null;
  last_connection_run: { ran_at: IsoDateTime; result: string; stats: Record<string, unknown> } | null;
}

export interface Batch {
  id: Id;
  external_id: string;
  kind: string;
  status: string;
  direction: string;
  issued_at: IsoDateTime | null;
  currency: string;
  declared_net_minor: Minor;
  computed_net_minor: Minor | null;
  delta_minor: Minor | null;
  result: ReconcileResult | null;
  ran_at: IsoDateTime | null;
  line_count: string;
  open_findings: string;
}

export interface Disbursement {
  external_id: string;
  status: string;
  amount_minor: Minor;
  currency: string;
  bank_trace: string | null;
  paid_at: IsoDateTime | null;
}

export interface LineComponent {
  group: string;
  native_name: string;
  fee_code: string | null;
  amount_minor: Minor;
  currency: string;
}

export interface Line {
  id: Id;
  external_id: string;
  settlement_status: string;
  line_kind: string;
  native_type: string;
  native_subtype: string | null;
  gross_minor: Minor;
  fee_minor: Minor;
  net_minor: Minor;
  currency: string;
  occurred_at: IsoDateTime;
  order_external_id: string | null;
  order_number: string | null;
  adjustment_class: string | null;
  adjustment_reason_text: string | null;
  is_test: boolean;
  components: LineComponent[];
  /** null unless this line is a REFUND. Shopify never returns the fee it kept on a PARTIAL refund. */
  refund_scope: "FULL" | "PARTIAL" | null;
  /** The fee the platform kept, signed like every other fee. Set only when refund_scope === "FULL". */
  retained_fee_minor: Minor | null;
}

export interface Discrepancy {
  id: Id;
  discrepancy_key: string;
  kind: string;
  severity: Severity;
  status: DiscrepancyStatus;
  adjustment_class: string | null;
  amount_minor: Minor | null;
  currency: string | null;
  explanation: string;
  note: string | null;
  batch_id: Id | null;
  batch_external_id: string | null;
  line_id: Id | null;
  line_external_id: string | null;
  order_number: string | null;
  dispute_external_id: string | null;
  first_seen_at: IsoDateTime;
  last_seen_at: IsoDateTime;
}

export interface DiscrepancyGroup {
  kind: string;
  severity: Severity;
  adjustment_class: string | null;
  count: string;
  amount_minor: Minor | null;
  currency: string | null;
}

export interface Dispute {
  id: Id;
  external_id: string;
  type: string;
  status: string;
  amount_minor: Minor;
  currency: string;
  reason_code: string | null;
  network_reason_code: string | null;
  initiated_at: IsoDateTime;
  evidence_due_by: IsoDateTime | null;
  evidence_sent_at: IsoDateTime | null;
  finalized_at: IsoDateTime | null;
  order_external_id: string | null;
  order_number: string | null;
  hold_status: string | null;
  hold_amount_minor: Minor | null;
  fee_minor: Minor | null;
}

export interface HeldFund {
  id: Id;
  kind: string;
  status: string;
  external_ref: string;
  amount_minor: Minor;
  currency: string;
  held_at: IsoDateTime;
  expected_release_at: IsoDateTime | null;
  released_at: IsoDateTime | null;
  dispute_external_id: string | null;
  order_number: string | null;
}

/**
 * What R8 compares a real settlement line against. `effective_to === null` marks the current,
 * open-ended row; every superseded row keeps the date range it was actually in force for, so a
 * past line stays judged by the rate that applied on the day it was cut (CLAUDE.md: a new rate
 * never rewrites history).
 */
export interface FeeSchedule {
  id: Id;
  line_kind: string;
  /** The platform charge this row prices, or null for every line of the kind. */
  native_subtype: string | null;
  /** Basis points, e.g. 290 for 2.9%. A plain number, not a bigint field. */
  rate_bps: number;
  flat_minor: Minor;
  currency: string;
  effective_from: IsoDateTime;
  effective_to: IsoDateTime | null;
  /** Free-text provenance, e.g. "Shopify Basic US plan" — a rate nobody can trace. */
  source: string;
}

/** POST body for a new fee-schedule row; the API supersedes whatever row was current for this
 *  (`line_kind`, `native_subtype`) timeline — naming a subtype never disturbs the catch-all. */
export interface NewFeeSchedule {
  line_kind: string;
  native_subtype?: string | null;
  rate_bps: number;
  flat_minor: Minor;
  currency: string;
  effective_from: string;
  source: string;
}

export interface BatchDetail {
  batch: Batch & { declared_summary: Record<string, unknown>; disbursement: Disbursement | null };
  lines: Line[];
  discrepancies: Discrepancy[];
}

export class ApiError extends Error {
  constructor(readonly status: number, message: string) {
    super(message);
    this.name = "ApiError";
  }
}

/** Set by the session provider; every 401 anywhere funnels through it exactly once. */
let unauthorizedHandler: (() => void) | null = null;
export function setUnauthorizedHandler(handler: (() => void) | null): void {
  unauthorizedHandler = handler;
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`${API_URL}${path}`, { credentials: "include", ...init });
  } catch {
    throw new ApiError(0, "Cannot reach the PayoutAudit API. Check your connection and try again.");
  }
  if (res.status === 401) {
    unauthorizedHandler?.();
    throw new ApiError(401, "Your session has expired. Sign in again.");
  }
  if (!res.ok) throw new ApiError(res.status, await errorMessage(res));
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

async function errorMessage(res: Response): Promise<string> {
  try {
    const body = (await res.json()) as { error?: string };
    if (typeof body.error === "string") return body.error;
  } catch {
    /* non-JSON error body — fall through to the status line */
  }
  return `Request failed (HTTP ${res.status}).`;
}

function query(params: Record<string, string | number | boolean | undefined>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined) search.set(key, String(value));
  }
  const qs = search.toString();
  return qs ? `?${qs}` : "";
}

const jsonPost = (body: unknown): RequestInit => ({
  method: "POST",
  headers: { "content-type": "application/json" },
  body: JSON.stringify(body),
});

export const api = {
  me: () => request<{ user: User }>("/auth/me"),
  requestLogin: (email: string) => request<{ ok: true }>("/auth/request", jsonPost({ email })),
  logout: () => request<void>("/auth/logout", { method: "POST" }),

  connections: () => request<{ connections: Connection[] }>("/api/v1/connections"),
  connection: (id: Id) => request<{ connection: Connection; summary: ConnectionSummary }>(`/api/v1/connections/${id}`),
  payouts: (id: Id, opts: { limit?: number; before?: Id } = {}) =>
    request<{ payouts: Batch[] }>(`/api/v1/connections/${id}/payouts${query({ limit: opts.limit, before: opts.before })}`),
  payout: (id: Id, batchId: Id) => request<BatchDetail>(`/api/v1/connections/${id}/payouts/${batchId}`),
  discrepancies: (id: Id, opts: { status?: DiscrepancyStatus; includeAutoClosed?: boolean; limit?: number } = {}) =>
    request<{ by_kind: DiscrepancyGroup[]; items: Discrepancy[] }>(
      `/api/v1/connections/${id}/discrepancies${query({ status: opts.status, include_auto_closed: opts.includeAutoClosed, limit: opts.limit })}`,
    ),
  disputes: (id: Id, opts: { open?: boolean } = {}) =>
    request<{ disputes: Dispute[] }>(`/api/v1/connections/${id}/disputes${query({ open: opts.open })}`),
  heldFunds: (id: Id, opts: { held?: boolean } = {}) =>
    request<{ held_funds: HeldFund[] }>(`/api/v1/connections/${id}/held-funds${query({ held: opts.held })}`),
  unsettled: (id: Id, opts: { limit?: number } = {}) =>
    request<{ lines: Line[] }>(`/api/v1/connections/${id}/unsettled${query({ limit: opts.limit })}`),

  feeSchedule: (id: Id) => request<{ schedules: FeeSchedule[] }>(`/api/v1/connections/${id}/fee-schedule`),
  createFeeSchedule: (id: Id, body: NewFeeSchedule) =>
    request<{ schedule: FeeSchedule; superseded_id: Id | null }>(`/api/v1/connections/${id}/fee-schedule`, jsonPost(body)),

  /** `refresh` re-reads the charge from Shopify before answering — the slow, authoritative form. */
  billing: (id: Id, opts: { refresh?: boolean } = {}) =>
    request<BillingResponse>(`/api/v1/connections/${id}/billing${query({ refresh: opts.refresh })}`),
  subscribe: (id: Id) =>
    request<{ confirmation_url: string; subscription: Subscription | null }>(`/api/v1/connections/${id}/billing/subscribe`, { method: "POST" }),
  cancelSubscription: (id: Id) =>
    request<{ subscription: Subscription }>(`/api/v1/connections/${id}/billing/cancel`, { method: "POST" }),
};

export interface BillingResponse {
  plan: BillingPlan;
  /** null = no charge has ever been created for this store. */
  subscription: Subscription | null;
}

/**
 * Store connect is a full-page navigation, not fetch: the API needs the browser to carry the
 * session cookie into the Shopify OAuth round trip so the shop lands on this seller's tenant.
 */
export function shopifyInstallUrl(shopDomain: string): string {
  return `${API_URL}/oauth/shopify/install${query({ shop: shopDomain })}`;
}

/** `<store>.myshopify.com`, the only form the API's install route accepts. */
export function normalizeShopDomain(input: string): string {
  const bare = input.trim().toLowerCase().replace(/^https?:\/\//, "").replace(/\/.*$/, "");
  return bare.endsWith(".myshopify.com") || bare === "" ? bare : `${bare}.myshopify.com`;
}

export function isValidShopDomain(domain: string): boolean {
  return /^[a-z0-9][a-z0-9-]*\.myshopify\.com$/.test(domain);
}
